import { Fragment, useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import api from "@/lib/api";
import { toast } from "sonner";
import { Shield, Loader2, Plus, Trash2, Edit3, Save, X, Tag, CreditCard, TrendingUp, Users, Calendar, Euro, CheckCircle2, AlertCircle, Sparkles, BarChart3, UserCheck, Clock, Ban, BadgeCheck, MapPin, Globe, Mail, Phone, Database, Receipt, Search, Building2, Copy } from "lucide-react";

const TABS = [
  { key: "stats", label: "Statistiche", icon: BarChart3 },
  { key: "plans", label: "Piani & Prezzi", icon: CreditCard },
  { key: "promos", label: "Promozioni", icon: Tag },
  { key: "professionals", label: "Professionisti", icon: UserCheck },
  { key: "customers", label: "Clienti & Fatturazione", icon: Receipt },
];

export default function AdminCMS() {
  const navigate = useNavigate();
  const [tab, setTab] = useState("stats");
  const [isAdmin, setIsAdmin] = useState(null);

  useEffect(() => {
    api.get("/cms/check-admin").then((r) => {
      if (!r.data?.is_admin) {
        toast.error("Accesso riservato al super admin");
        navigate("/dashboard");
        return;
      }
      setIsAdmin(true);
    }).catch(() => {
      toast.error("Errore verifica permessi");
      navigate("/dashboard");
    });
  }, [navigate]);

  if (isAdmin === null) {
    return <div className="flex items-center justify-center min-h-[400px]"><Loader2 className="w-6 h-6 animate-spin text-[#2563EB]" /></div>;
  }

  return (
    <div className="space-y-8" data-testid="admin-cms-page">
      <div className="flex items-center justify-between flex-wrap gap-3">
        <div>
          <div className="flex items-center gap-2">
            <span className="px-2 py-0.5 rounded-full bg-amber-200 text-amber-900 text-[10px] font-bold uppercase tracking-wider">Super Admin</span>
            <p className="text-xs uppercase tracking-[0.18em] text-[#2563EB] font-semibold font-ui">CMS</p>
          </div>
          <h1 className="font-display text-4xl md:text-5xl text-[#0F2741] tracking-tight mt-2 flex items-center gap-3">
            <Shield className="w-9 h-9 text-amber-500" /> Pannello di gestione
          </h1>
          <p className="text-slate-500 mt-2">Modifica piani, prezzi, promozioni e visualizza le statistiche della piattaforma.</p>
        </div>
        <SeedAllButton />
      </div>

      <div className="flex gap-2 border-b border-slate-200">
        {TABS.map((t) => {
          const Icon = t.icon;
          return (
            <button
              key={t.key}
              onClick={() => setTab(t.key)}
              data-testid={`admin-tab-${t.key}`}
              className={`px-5 py-3 font-ui font-semibold text-sm flex items-center gap-2 border-b-2 -mb-px transition-colors ${tab === t.key ? "border-[#2563EB] text-[#2563EB]" : "border-transparent text-slate-500 hover:text-[#0F2741]"}`}
            >
              <Icon className="w-4 h-4" /> {t.label}
            </button>
          );
        })}
      </div>

      {tab === "stats" && <StatsTab />}
      {tab === "plans" && <PlansTab />}
      {tab === "promos" && <PromosTab />}
      {tab === "professionals" && <ProfessionalsTab />}
      {tab === "customers" && <CustomersTab />}
    </div>
  );
}

function ProfessionalsTab() {
  const [statusFilter, setStatusFilter] = useState("pending");
  const [data, setData] = useState({ artists: [], suppliers: [] });
  const [loading, setLoading] = useState(true);
  const [kindFilter, setKindFilter] = useState("all"); // all|artist|supplier

  const load = () => {
    setLoading(true);
    api.get(`/admin/professionals?status=${statusFilter}`)
      .then((r) => { setData(r.data); setLoading(false); })
      .catch(() => { setLoading(false); });
  };
  useEffect(load, [statusFilter]); // eslint-disable-line react-hooks/exhaustive-deps

  const act = async (kind, id, action, name) => {
    const confirms = {
      approve: `Approvare il profilo "${name}"?`,
      suspend: `Sospendere il profilo "${name}"? Non sarà più visibile ai ristoratori.`,
      unsuspend: `Riattivare il profilo "${name}"?`,
      reject: `RIFIUTARE ed eliminare definitivamente il profilo "${name}"? Azione irreversibile.`,
    };
    if (!window.confirm(confirms[action])) return;
    try {
      await api.post(`/admin/professionals/${kind}/${id}/${action}`);
      toast.success(action === "approve" ? "Approvato ✓" : action === "suspend" ? "Sospeso" : action === "unsuspend" ? "Riattivato ✓" : "Eliminato");
      load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Errore");
    }
  };

  const allItems = [
    ...data.artists.map((a) => ({ ...a, _kind: "artist" })),
    ...data.suppliers.map((s) => ({ ...s, _kind: "supplier" })),
  ].filter((x) => kindFilter === "all" || x._kind === kindFilter);

  const STATUS_TABS = [
    { key: "pending", label: "In attesa", icon: Clock, color: "amber" },
    { key: "approved", label: "Approvati", icon: BadgeCheck, color: "sky" },
    { key: "suspended", label: "Sospesi", icon: Ban, color: "rose" },
  ];

  return (
    <div className="space-y-5" data-testid="professionals-tab">
      <div className="flex flex-wrap items-center gap-2">
        <div className="inline-flex bg-white rounded-lg border border-slate-200 p-1">
          {STATUS_TABS.map((s) => {
            const Icon = s.icon;
            const active = statusFilter === s.key;
            return (
              <button key={s.key} onClick={() => setStatusFilter(s.key)} data-testid={`status-tab-${s.key}`}
                className={`px-4 py-2 rounded-md text-sm font-ui font-semibold transition-all inline-flex items-center gap-1.5 ${active ? `bg-${s.color}-100 text-${s.color}-700` : "text-slate-500 hover:text-[#0F2741]"}`}>
                <Icon className="w-3.5 h-3.5" /> {s.label}
              </button>
            );
          })}
        </div>
        <select value={kindFilter} onChange={(e) => setKindFilter(e.target.value)} data-testid="kind-filter" className="px-3 py-2 rounded-lg border border-slate-200 text-sm bg-white">
          <option value="all">Tutti i tipi</option>
          <option value="artist">Solo artisti/fotografi</option>
          <option value="supplier">Solo fornitori</option>
        </select>
        <span className="ml-auto text-sm text-slate-500">{allItems.length} profili</span>
      </div>

      {loading ? (
        <div className="flex items-center gap-2 text-slate-400"><Loader2 className="w-4 h-4 animate-spin" /> Caricamento…</div>
      ) : allItems.length === 0 ? (
        <div className="bg-white rounded-xl border border-dashed border-slate-300 p-12 text-center">
          <UserCheck className="w-12 h-12 mx-auto text-slate-300 mb-3" />
          <p className="text-slate-500">Nessun profilo in stato "{STATUS_TABS.find((s) => s.key === statusFilter)?.label}".</p>
        </div>
      ) : (
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          {allItems.map((p) => (
            <div key={`${p._kind}-${p.id}`} className="bg-white rounded-xl border border-slate-200 p-5" data-testid={`pro-card-${p.id}`}>
              <div className="flex items-start justify-between gap-2">
                <div>
                  <div className="flex items-center gap-2 flex-wrap">
                    <h4 className="font-ui font-bold text-[#0F2741]">{p.name}</h4>
                    <span className="text-[10px] uppercase px-1.5 py-0.5 rounded-full bg-slate-100 text-slate-600 font-semibold">{p._kind === "artist" ? "Artista" : "Fornitore"}</span>
                  </div>
                  {p.category && <p className="text-xs uppercase tracking-wider text-[#1D4ED8] mt-0.5">{p.category}{p.specialty ? ` · ${p.specialty}` : ""}</p>}
                </div>
                <span className={`text-xs px-2 py-1 rounded-full font-semibold shrink-0 ${p.status === "pending" ? "bg-amber-100 text-amber-700" : p.status === "approved" ? "bg-sky-100 text-sky-700" : "bg-rose-100 text-rose-700"}`}>
                  {p.status === "pending" ? "In attesa" : p.status === "approved" ? "Approvato" : "Sospeso"}
                </span>
              </div>
              <div className="mt-2 space-y-1 text-xs text-slate-500">
                {p.email && <a href={`mailto:${p.email}`} className="inline-flex items-center gap-1 hover:text-[#2563EB]"><Mail className="w-3 h-3" /> {p.email}</a>}
                {p.phone && <p className="inline-flex items-center gap-1 ml-3"><Phone className="w-3 h-3" /> {p.phone}</p>}
                {(p.city || p.region) && <p className="inline-flex items-center gap-1"><MapPin className="w-3 h-3" /> {[p.city, p.region].filter(Boolean).join(" · ")}</p>}
                {p.website && <a href={p.website} target="_blank" rel="noreferrer" className="inline-flex items-center gap-1 hover:text-[#2563EB]"><Globe className="w-3 h-3" /> {p.website.replace(/^https?:\/\//, "")}</a>}
              </div>
              {(p.price_eur || p.avg_order_eur) ? (
                <p className="text-xs text-[#1D4ED8] mt-1 font-semibold">{p._kind === "artist" ? `€${p.price_eur} / evento` : `€${p.avg_order_eur} / ordine medio`}</p>
              ) : null}
              {p.description && <p className="text-xs text-slate-500 mt-2 line-clamp-2">{p.description}</p>}
              <div className="mt-4 pt-3 border-t border-slate-100 flex flex-wrap gap-2">
                {p.status === "pending" && (
                  <>
                    <button onClick={() => act(p._kind, p.id, "approve", p.name)} data-testid={`btn-approve-${p.id}`} className="btn-primary text-xs px-3 py-1.5 bg-emerald-600 hover:bg-emerald-700">
                      <CheckCircle2 className="w-3.5 h-3.5" /> Approva
                    </button>
                    <button onClick={() => act(p._kind, p.id, "reject", p.name)} data-testid={`btn-reject-${p.id}`} className="btn-ghost text-rose-600 text-xs px-3 py-1.5">
                      <X className="w-3.5 h-3.5" /> Rifiuta
                    </button>
                  </>
                )}
                {p.status === "approved" && (
                  <button onClick={() => act(p._kind, p.id, "suspend", p.name)} data-testid={`btn-suspend-${p.id}`} className="btn-ghost text-rose-600 border border-rose-200 text-xs px-3 py-1.5">
                    <Ban className="w-3.5 h-3.5" /> Sospendi
                  </button>
                )}
                {p.status === "suspended" && (
                  <button onClick={() => act(p._kind, p.id, "unsuspend", p.name)} data-testid={`btn-unsuspend-${p.id}`} className="btn-primary text-xs px-3 py-1.5 bg-emerald-600 hover:bg-emerald-700">
                    <CheckCircle2 className="w-3.5 h-3.5" /> Riattiva
                  </button>
                )}
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

function StatsTab() {
  const [stats, setStats] = useState(null);
  useEffect(() => { api.get("/cms/stats").then((r) => setStats(r.data)).catch(() => {}); }, []);
  if (!stats) return <div className="flex items-center gap-2 text-slate-400"><Loader2 className="w-4 h-4 animate-spin" /> Caricamento...</div>;
  const cards = [
    { label: "Utenti totali", value: stats.total_users, icon: Users, color: "#2563EB" },
    { label: "Eventi creati", value: stats.total_events, icon: Calendar, color: "#0EA5E9" },
    { label: "Abbonamenti attivi", value: stats.active_subscriptions, icon: CreditCard, color: "#10B981" },
    { label: "Preventivi inviati", value: stats.total_quotes, icon: Sparkles, color: "#8B5CF6" },
    { label: "Preventivi accettati", value: stats.total_approved, icon: CheckCircle2, color: "#10B981" },
    { label: "Fatturato totale", value: `€ ${stats.total_revenue_eur.toLocaleString("it-IT", { minimumFractionDigits: 2 })}`, icon: TrendingUp, color: "#F59E0B" },
  ];
  return (
    <div className="grid grid-cols-1 md:grid-cols-3 gap-5" data-testid="stats-tab">
      {cards.map((c) => (
        <div key={c.label} className="bg-white border border-slate-200 rounded-xl p-5">
          <div className="flex items-center justify-between mb-2">
            <p className="text-xs uppercase tracking-wider text-slate-500 font-semibold font-ui">{c.label}</p>
            <c.icon className="w-4 h-4" style={{ color: c.color }} />
          </div>
          <p className="font-display text-3xl text-[#0F2741]">{c.value}</p>
        </div>
      ))}
    </div>
  );
}

function PlansTab() {
  const [plans, setPlans] = useState([]);
  const [editing, setEditing] = useState(null);
  const [adding, setAdding] = useState(false);

  const load = () => api.get("/cms/plans").then((r) => setPlans(r.data.plans || []));
  useEffect(() => { load(); }, []);

  const save = async (plan) => {
    try {
      await api.put(`/cms/plans/${plan.key}`, plan);
      toast.success("Piano aggiornato");
      setEditing(null);
      setAdding(false);
      await load();
    } catch (e) { toast.error(e?.response?.data?.detail || "Errore"); }
  };

  const reset = async (key) => {
    if (!window.confirm(`Ripristinare le impostazioni default per il piano "${key}"?`)) return;
    await api.delete(`/cms/plans/${key}`);
    toast.success("Piano ripristinato");
    await load();
  };

  return (
    <div className="space-y-4" data-testid="plans-tab">
      <div className="flex justify-between items-center">
        <p className="text-sm text-slate-500">Modifica nome, prezzo, descrizione e features dei piani esistenti, oppure crea nuove offerte (es. pacchetti crediti AI).</p>
        <button onClick={() => setAdding(true)} data-testid="btn-new-plan" className="btn-primary"><Plus className="w-4 h-4" /> Nuovo piano</button>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
        {plans.map((p) => (
          <div key={p.key} className="bg-white border-2 border-slate-200 rounded-xl p-5">
            <div className="flex justify-between items-start mb-2">
              <div>
                <p className="text-[10px] uppercase tracking-wider text-slate-400 font-semibold">{p.key} · {p.billing || "monthly"} · {p.category || "subscription"}</p>
                <p className="font-display text-2xl text-[#0F2741]">{p.name}</p>
              </div>
              <p className="font-display text-2xl text-[#2563EB]">€{Number(p.price_eur).toFixed(2)}</p>
            </div>
            <p className="text-sm text-slate-500 mb-2">{p.description}</p>
            <ul className="text-xs text-slate-600 space-y-1 mb-3">
              {(p.features || []).map((f, i) => (<li key={i} className="flex gap-1.5"><CheckCircle2 className="w-3.5 h-3.5 text-emerald-500 shrink-0 mt-0.5" /> {f}</li>))}
            </ul>
            <div className="flex gap-2">
              <button onClick={() => setEditing(p)} data-testid={`btn-edit-plan-${p.key}`} className="btn-ghost border border-slate-200 text-xs"><Edit3 className="w-3 h-3" /> Modifica</button>
              <button onClick={() => reset(p.key)} className="btn-ghost border border-rose-200 text-rose-600 text-xs"><Trash2 className="w-3 h-3" /> Reset</button>
            </div>
          </div>
        ))}
      </div>

      {(editing || adding) && (
        <PlanEditor
          initial={editing || { key: "", name: "", description: "", price_eur: 0, features: [], billing: "monthly", category: "subscription" }}
          isNew={adding}
          onClose={() => { setEditing(null); setAdding(false); }}
          onSave={save}
        />
      )}
    </div>
  );
}

function PlanEditor({ initial, isNew, onClose, onSave }) {
  const [p, setP] = useState({ ...initial, features: [...(initial.features || [])] });
  const submit = (e) => {
    e.preventDefault();
    if (!p.key || !p.name) { toast.error("Key e nome obbligatori"); return; }
    onSave(p);
  };
  return (
    <div className="fixed inset-0 bg-black/40 z-50 grid place-items-center p-4" onClick={onClose}>
      <form onSubmit={submit} onClick={(e) => e.stopPropagation()} className="bg-white rounded-xl p-6 w-full max-w-xl space-y-4 max-h-[90vh] overflow-y-auto" data-testid="plan-editor">
        <div className="flex justify-between items-center">
          <h3 className="font-display text-2xl text-[#0F2741]">{isNew ? "Nuovo piano" : `Modifica ${p.name}`}</h3>
          <button type="button" onClick={onClose}><X className="w-5 h-5 text-slate-400" /></button>
        </div>
        <div className="grid grid-cols-2 gap-3">
          <Field label="Key (univoco)" disabled={!isNew}>
            <input value={p.key} disabled={!isNew} onChange={(e) => setP({ ...p, key: e.target.value.toLowerCase().replace(/[^a-z0-9_]/g, "_") })} className="input" placeholder="es. ai_credits_50" />
          </Field>
          <Field label="Nome visibile"><input value={p.name} onChange={(e) => setP({ ...p, name: e.target.value })} className="input" placeholder="Pacchetto AI 50 crediti" /></Field>
          <Field label="Prezzo (€)"><input type="number" step="0.01" min="0" value={p.price_eur} onChange={(e) => setP({ ...p, price_eur: parseFloat(e.target.value) || 0 })} className="input font-semibold" /></Field>
          <Field label="Ricorrenza">
            <select value={p.billing || "monthly"} onChange={(e) => setP({ ...p, billing: e.target.value })} className="input">
              <option value="monthly">Mensile (abbonamento)</option>
              <option value="yearly">Annuale</option>
              <option value="one_time">Una tantum</option>
            </select>
          </Field>
          <Field label="Categoria">
            <select value={p.category || "subscription"} onChange={(e) => setP({ ...p, category: e.target.value })} className="input">
              <option value="subscription">Abbonamento</option>
              <option value="credit_pack">Pacchetto crediti AI</option>
              <option value="addon">Add-on</option>
              <option value="other">Altro</option>
            </select>
          </Field>
        </div>
        <Field label="Descrizione"><textarea rows={2} value={p.description || ""} onChange={(e) => setP({ ...p, description: e.target.value })} className="input" /></Field>
        <div>
          <label className="text-xs uppercase tracking-wider text-slate-500 font-semibold mb-1 block">Features</label>
          <div className="space-y-1.5">
            {p.features.map((f, i) => (
              <div key={i} className="flex gap-2">
                <input value={f} onChange={(e) => { const fs = [...p.features]; fs[i] = e.target.value; setP({ ...p, features: fs }); }} className="input flex-1 text-sm" />
                <button type="button" onClick={() => setP({ ...p, features: p.features.filter((_, x) => x !== i) })} className="text-rose-400 hover:text-rose-600"><Trash2 className="w-4 h-4" /></button>
              </div>
            ))}
            <button type="button" onClick={() => setP({ ...p, features: [...p.features, ""] })} className="btn-ghost border border-dashed border-slate-300 w-full text-xs"><Plus className="w-3 h-3" /> Aggiungi feature</button>
          </div>
        </div>
        <div className="flex justify-end gap-2 pt-3 border-t border-slate-100">
          <button type="button" onClick={onClose} className="btn-ghost">Annulla</button>
          <button type="submit" data-testid="btn-save-plan" className="btn-primary"><Save className="w-4 h-4" /> Salva</button>
        </div>
      </form>
    </div>
  );
}

function PromosTab() {
  const [promos, setPromos] = useState([]);
  const [editing, setEditing] = useState(null);

  const load = () => api.get("/cms/promos").then((r) => setPromos(r.data.promos || []));
  useEffect(() => { load(); }, []);

  const save = async (promo) => {
    try {
      if (promo.id) await api.put(`/cms/promos/${promo.id}`, promo);
      else await api.post("/cms/promos", promo);
      toast.success("Promo salvata");
      setEditing(null);
      await load();
    } catch (e) { toast.error(e?.response?.data?.detail || "Errore"); }
  };

  const remove = async (id) => {
    if (!window.confirm("Eliminare questa promozione?")) return;
    await api.delete(`/cms/promos/${id}`);
    toast.success("Promo eliminata");
    await load();
  };

  const toggleActive = async (p) => {
    await api.put(`/cms/promos/${p.id}`, { active: !p.active });
    await load();
  };

  const now = new Date().toISOString();
  return (
    <div className="space-y-4" data-testid="promos-tab">
      <div className="flex justify-between items-center">
        <p className="text-sm text-slate-500">Crea promozioni a tempo limitato con codice opzionale (sconto %, sconto fisso o crediti gratis).</p>
        <button onClick={() => setEditing({})} data-testid="btn-new-promo" className="btn-primary"><Plus className="w-4 h-4" /> Nuova promo</button>
      </div>

      {promos.length === 0 ? (
        <div className="text-center py-12 text-slate-400 border-2 border-dashed border-slate-200 rounded-xl">
          <Tag className="w-10 h-10 mx-auto mb-2 opacity-40" />
          <p>Nessuna promozione configurata</p>
        </div>
      ) : (
        <div className="overflow-x-auto">
          <table className="w-full text-sm">
            <thead className="text-xs uppercase tracking-wider text-slate-500">
              <tr className="border-b border-slate-200">
                <th className="text-left px-3 py-2">Promo</th>
                <th className="text-left px-3 py-2">Codice</th>
                <th className="text-left px-3 py-2">Sconto</th>
                <th className="text-left px-3 py-2">Validità</th>
                <th className="text-left px-3 py-2">Stato</th>
                <th className="text-right px-3 py-2">Azioni</th>
              </tr>
            </thead>
            <tbody>
              {promos.map((p) => {
                const isExpired = p.valid_to && p.valid_to < now;
                const isFuture = p.valid_from && p.valid_from > now;
                return (
                  <tr key={p.id} className="border-b border-slate-100 hover:bg-slate-50">
                    <td className="px-3 py-3"><div className="font-ui font-semibold text-[#0F2741]">{p.label}</div>{p.description && <div className="text-xs text-slate-500">{p.description}</div>}</td>
                    <td className="px-3 py-3">{p.code ? <code className="bg-slate-100 px-2 py-0.5 rounded text-xs font-bold">{p.code}</code> : <span className="text-slate-400 text-xs">—</span>}</td>
                    <td className="px-3 py-3">
                      {p.discount_type === "percentage" && <span className="font-semibold text-emerald-600">-{p.discount_value}%</span>}
                      {p.discount_type === "fixed" && <span className="font-semibold text-emerald-600">-€{p.discount_value}</span>}
                      {p.discount_type === "free_credits" && <span className="font-semibold text-purple-600">+{p.discount_value} crediti</span>}
                    </td>
                    <td className="px-3 py-3 text-xs text-slate-500">
                      {p.valid_from ? new Date(p.valid_from).toLocaleDateString("it-IT") : "—"} → {p.valid_to ? new Date(p.valid_to).toLocaleDateString("it-IT") : "—"}
                    </td>
                    <td className="px-3 py-3">
                      <button onClick={() => toggleActive(p)} className={`text-xs px-2 py-0.5 rounded-full font-semibold ${
                        !p.active ? "bg-slate-200 text-slate-600" :
                        isExpired ? "bg-rose-100 text-rose-700" :
                        isFuture ? "bg-amber-100 text-amber-700" :
                        "bg-emerald-100 text-emerald-700"
                      }`}>
                        {!p.active ? "Disattiva" : isExpired ? "Scaduta" : isFuture ? "Futura" : "Attiva"}
                      </button>
                    </td>
                    <td className="px-3 py-3 text-right">
                      <button onClick={() => setEditing(p)} data-testid={`btn-edit-promo-${p.id}`} className="text-[#2563EB] hover:text-[#1D4ED8] mr-2"><Edit3 className="w-4 h-4" /></button>
                      <button onClick={() => remove(p.id)} className="text-rose-400 hover:text-rose-600"><Trash2 className="w-4 h-4" /></button>
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      )}

      {editing !== null && <PromoEditor initial={editing} onClose={() => setEditing(null)} onSave={save} />}
    </div>
  );
}

function PromoEditor({ initial, onClose, onSave }) {
  const [availablePlans, setAvailablePlans] = useState([]);
  const [p, setP] = useState({
    label: initial.label || "",
    code: initial.code || "",
    discount_type: initial.discount_type || "percentage",
    discount_value: initial.discount_value ?? 10,
    valid_from: initial.valid_from ? initial.valid_from.split("T")[0] : new Date().toISOString().split("T")[0],
    valid_to: initial.valid_to ? initial.valid_to.split("T")[0] : new Date(Date.now() + 30 * 86400000).toISOString().split("T")[0],
    active: initial.active !== false,
    description: initial.description || "",
    priority: initial.priority || 0,
    applicable_plans: initial.applicable_plans || [],
  });

  useEffect(() => {
    api.get("/cms/plans").then((r) => setAvailablePlans(r.data.plans || [])).catch(() => {});
  }, []);

  const applyToAll = (p.applicable_plans || []).length === 0;

  const togglePlan = (key) => {
    if (applyToAll) {
      // switch to specific selection mode, start with only this plan
      setP({ ...p, applicable_plans: [key] });
    } else {
      const next = p.applicable_plans.includes(key)
        ? p.applicable_plans.filter((k) => k !== key)
        : [...p.applicable_plans, key];
      setP({ ...p, applicable_plans: next });
    }
  };

  const submit = (e) => {
    e.preventDefault();
    onSave({
      ...initial,
      ...p,
      valid_from: new Date(p.valid_from).toISOString(),
      valid_to: new Date(p.valid_to + "T23:59:59").toISOString(),
    });
  };

  return (
    <div className="fixed inset-0 bg-black/40 z-50 grid place-items-center p-4" onClick={onClose}>
      <form onSubmit={submit} onClick={(e) => e.stopPropagation()} className="bg-white rounded-xl p-6 w-full max-w-xl space-y-4 max-h-[92vh] overflow-y-auto" data-testid="promo-editor">
        <div className="flex justify-between items-center sticky top-0 bg-white pb-3 border-b border-slate-100">
          <h3 className="font-display text-2xl text-[#0F2741]">{initial.id ? "Modifica promo" : "Nuova promo"}</h3>
          <button type="button" onClick={onClose}><X className="w-5 h-5 text-slate-400" /></button>
        </div>
        <Field label="Nome promo"><input value={p.label} onChange={(e) => setP({ ...p, label: e.target.value })} required data-testid="input-promo-label" className="input" placeholder="Es. Lancio Estate 2026" /></Field>
        <div className="grid grid-cols-2 gap-3">
          <Field label="Codice (opzionale)"><input value={p.code} onChange={(e) => setP({ ...p, code: e.target.value.toUpperCase() })} className="input font-mono" placeholder="ESTATE25" /></Field>
          <Field label="Priorità (più alta = mostrata per prima)"><input type="number" min="0" value={p.priority} onChange={(e) => setP({ ...p, priority: parseInt(e.target.value) || 0 })} className="input" /></Field>
        </div>
        <div className="grid grid-cols-2 gap-3">
          <Field label="Tipo sconto">
            <select value={p.discount_type} onChange={(e) => setP({ ...p, discount_type: e.target.value })} className="input">
              <option value="percentage">Percentuale (%)</option>
              <option value="fixed">Sconto fisso (€)</option>
              <option value="free_credits">Crediti AI gratis</option>
            </select>
          </Field>
          <Field label={p.discount_type === "percentage" ? "Valore (%)" : p.discount_type === "fixed" ? "Valore (€)" : "Crediti"}>
            <input type="number" step={p.discount_type === "percentage" ? "1" : "0.50"} min="0" value={p.discount_value} onChange={(e) => setP({ ...p, discount_value: parseFloat(e.target.value) || 0 })} className="input font-semibold" />
          </Field>
        </div>

        {/* Applicable plans selector */}
        <div data-testid="applicable-plans-section">
          <label className="text-xs uppercase tracking-wider text-slate-500 font-semibold block mb-2">Applica la promo a</label>
          <div className="space-y-2">
            <label className="flex items-center gap-2 p-3 rounded-lg border-2 cursor-pointer hover:bg-slate-50" style={{ borderColor: applyToAll ? "#2563EB" : "#E2E8F0", background: applyToAll ? "#EFF6FF" : "white" }}>
              <input
                type="radio"
                checked={applyToAll}
                onChange={() => setP({ ...p, applicable_plans: [] })}
                data-testid="promo-apply-all"
                className="accent-[#2563EB]"
              />
              <span className="font-ui font-semibold text-[#0F2741]">Tutti i prodotti</span>
              <span className="text-xs text-slate-500">— la promo si applica automaticamente a ogni piano</span>
            </label>
            <label className="flex items-center gap-2 p-3 rounded-lg border-2 cursor-pointer hover:bg-slate-50" style={{ borderColor: !applyToAll ? "#2563EB" : "#E2E8F0", background: !applyToAll ? "#EFF6FF" : "white" }}>
              <input
                type="radio"
                checked={!applyToAll}
                onChange={() => { if (applyToAll && availablePlans.length) setP({ ...p, applicable_plans: [availablePlans[0].key] }); }}
                data-testid="promo-apply-specific"
                className="accent-[#2563EB]"
              />
              <span className="font-ui font-semibold text-[#0F2741]">Solo prodotti selezionati</span>
            </label>
            {!applyToAll && (
              <div className="ml-6 pl-3 border-l-2 border-slate-200 space-y-1.5">
                {availablePlans.map((pp) => (
                  <label key={pp.key} className="flex items-center gap-2 cursor-pointer py-1" data-testid={`promo-plan-${pp.key}`}>
                    <input
                      type="checkbox"
                      checked={p.applicable_plans.includes(pp.key)}
                      onChange={() => togglePlan(pp.key)}
                      className="accent-[#2563EB]"
                    />
                    <span className="text-sm text-slate-700">{pp.name}</span>
                    <span className="text-xs text-slate-400">— €{Number(pp.price_eur).toFixed(2)} {pp.billing === "one_time" ? "una tantum" : "/mese"}</span>
                  </label>
                ))}
                {availablePlans.length === 0 && <p className="text-xs text-slate-400 italic">Caricamento prodotti...</p>}
              </div>
            )}
          </div>
        </div>

        <div className="grid grid-cols-2 gap-3">
          <Field label="Valida da"><input type="date" value={p.valid_from} onChange={(e) => setP({ ...p, valid_from: e.target.value })} className="input" /></Field>
          <Field label="Valida fino"><input type="date" value={p.valid_to} onChange={(e) => setP({ ...p, valid_to: e.target.value })} className="input" /></Field>
        </div>
        <Field label="Descrizione (opzionale)"><textarea rows={2} value={p.description} onChange={(e) => setP({ ...p, description: e.target.value })} className="input" placeholder="Descrizione mostrata agli utenti..." /></Field>
        <label className="flex items-center gap-2 text-sm">
          <input type="checkbox" checked={p.active} onChange={(e) => setP({ ...p, active: e.target.checked })} className="accent-[#2563EB]" />
          Promo attiva
        </label>
        <div className="flex justify-end gap-2 pt-3 border-t border-slate-100 sticky bottom-0 bg-white">
          <button type="button" onClick={onClose} className="btn-ghost">Annulla</button>
          <button type="submit" data-testid="btn-save-promo" className="btn-primary"><Save className="w-4 h-4" /> Salva</button>
        </div>
      </form>
    </div>
  );
}

function Field({ label, children, disabled }) {
  return (
    <label className={`block ${disabled ? "opacity-60" : ""}`}>
      <span className="text-xs uppercase tracking-wider text-slate-500 font-semibold block mb-1">{label}</span>
      {children}
    </label>
  );
}

function SeedAllButton() {
  const [loading, setLoading] = useState(false);
  const [report, setReport] = useState(null);

  const run = async (force = false) => {
    const msg = force
      ? "FORCE: questo svuoterà ingredienti/piatti/artisti curati/fornitori curati e ri-popolerà tutto. Confermi?"
      : "Popolare ingredienti, piatti, artisti e fornitori mancanti? (Top-up sicuro, non cancella nulla)";
    if (!window.confirm(msg)) return;
    setLoading(true);
    try {
      const r = await api.post(`/admin/seed-all${force ? "?force=true" : ""}`);
      setReport(r.data);
      const counts = r.data.final_counts || {};
      toast.success(`Popolato: ${counts.ingredients} ingredienti, ${counts.dishes} piatti, ${counts.artists} artisti, ${counts.suppliers} fornitori`);
    } catch (e) {
      toast.error("Errore: " + (e?.response?.data?.detail || e.message));
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="flex flex-col items-end gap-2" data-testid="seed-all-panel">
      <div className="flex gap-2">
        <button onClick={() => run(false)} disabled={loading} data-testid="btn-seed-all" className="btn-ghost border border-emerald-300 text-emerald-700 bg-emerald-50 hover:bg-emerald-100">
          {loading ? <Loader2 className="w-4 h-4 animate-spin" /> : <Database className="w-4 h-4" />}
          Popola DB (top-up)
        </button>
        <button onClick={() => run(true)} disabled={loading} className="btn-ghost border border-rose-200 text-rose-600 hover:bg-rose-50 text-xs">
          Force reset
        </button>
      </div>
      {report && (
        <div className="text-xs text-slate-500 bg-white border border-slate-200 rounded-lg p-2 max-w-md">
          <p className="font-semibold text-emerald-700 mb-1">Operazioni:</p>
          <ul className="space-y-0.5">
            {(report.actions || []).map((a, i) => <li key={i}>• {a}</li>)}
          </ul>
        </div>
      )}
    </div>
  );
}



function CustomersTab() {
  const [users, setUsers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState("");
  const [withBillingOnly, setWithBillingOnly] = useState(false);
  const [expanded, setExpanded] = useState(null);

  const load = async () => {
    setLoading(true);
    try {
      const r = await api.get(`/cms/users?search=${encodeURIComponent(search)}&with_billing_only=${withBillingOnly}`);
      setUsers(r.data?.users || []);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Errore caricamento clienti");
    } finally {
      setLoading(false);
    }
  };
  useEffect(() => { load(); /* eslint-disable-next-line react-hooks/exhaustive-deps */ }, [withBillingOnly]);

  const copyAll = (bi) => {
    const txt = [
      `Ragione sociale: ${bi.company_name || "—"}`,
      `P.IVA / CF: ${bi.vat_or_cf || "—"}`,
      `Indirizzo: ${bi.address || "—"}`,
      `Email fattura: ${bi.billing_email || "—"}`,
      `PEC / SDI: ${bi.pec_or_sdi || "—"}`,
      `Telefono: ${bi.phone || "—"}`,
    ].join("\n");
    navigator.clipboard?.writeText(txt);
    toast.success("Dati copiati negli appunti");
  };

  const exportCsv = () => {
    const header = ["Email","Nome","Piano","Stato","Eventi","RagioneSociale","PIVA_CF","Indirizzo","EmailFattura","PEC_SDI","Telefono","Registrato"];
    const rows = users.map((u) => [
      u.email, u.name, u.plan, u.subscription_status, u.events_count,
      u.billing_info?.company_name || "", u.billing_info?.vat_or_cf || "",
      u.billing_info?.address || "", u.billing_info?.billing_email || "",
      u.billing_info?.pec_or_sdi || "", u.billing_info?.phone || "",
      u.created_at || "",
    ]);
    const csv = [header, ...rows].map((r) => r.map((x) => `"${String(x ?? "").replace(/"/g, '""')}"`).join(",")).join("\n");
    const blob = new Blob([csv], { type: "text/csv;charset=utf-8" });
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url;
    a.download = `clienti-${new Date().toISOString().split("T")[0]}.csv`;
    a.click();
    URL.revokeObjectURL(url);
  };

  const planBadgeClass = (plan) => {
    if (plan === "premium") return "bg-amber-100 text-amber-800 border-amber-300";
    if (plan === "base") return "bg-blue-100 text-blue-800 border-blue-300";
    return "bg-slate-100 text-slate-700 border-slate-300";
  };

  return (
    <div className="space-y-4" data-testid="customers-tab">
      <div className="flex items-center justify-between gap-3 flex-wrap">
        <div>
          <h2 className="font-display text-2xl text-[#0F2741] flex items-center gap-2"><Receipt className="w-6 h-6 text-[#2563EB]" /> Clienti e dati di fatturazione</h2>
          <p className="text-sm text-slate-500 mt-1">Visualizza tutti i ristoratori iscritti e i relativi dati fiscali per la fatturazione.</p>
        </div>
        <button onClick={exportCsv} disabled={!users.length} className="btn-ghost border border-slate-200 inline-flex items-center gap-2 disabled:opacity-50" data-testid="btn-export-customers-csv">
          <Database className="w-4 h-4" /> Esporta CSV
        </button>
      </div>

      <div className="flex items-center gap-3 flex-wrap bg-slate-50 rounded-xl p-3 border border-slate-200">
        <div className="flex-1 min-w-[240px] relative">
          <Search className="w-4 h-4 absolute left-3 top-1/2 -translate-y-1/2 text-slate-400" />
          <input
            type="search"
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            onKeyDown={(e) => e.key === "Enter" && load()}
            placeholder="Cerca per email, nome, ragione sociale o P.IVA..."
            className="w-full pl-9 pr-3 py-2 rounded-lg border border-slate-200 text-sm"
            data-testid="customers-search-input"
          />
        </div>
        <button onClick={load} className="btn-primary inline-flex items-center gap-2" data-testid="btn-search-customers">
          <Search className="w-4 h-4" /> Cerca
        </button>
        <label className="flex items-center gap-2 cursor-pointer text-sm">
          <input
            type="checkbox"
            checked={withBillingOnly}
            onChange={(e) => setWithBillingOnly(e.target.checked)}
            className="w-4 h-4"
            data-testid="customers-billing-only-toggle"
          />
          <span className="text-slate-700">Solo con dati fatturazione</span>
        </label>
      </div>

      {loading ? (
        <div className="text-center py-12 text-slate-400"><Loader2 className="w-6 h-6 animate-spin mx-auto" /></div>
      ) : users.length === 0 ? (
        <div className="text-center py-16 text-slate-500 bg-white rounded-xl border border-slate-200">
          <Receipt className="w-12 h-12 mx-auto text-slate-300 mb-3" />
          <p>Nessun cliente trovato.</p>
        </div>
      ) : (
        <div className="bg-white rounded-xl border border-slate-200 overflow-hidden">
          <div className="overflow-x-auto">
            <table className="w-full text-sm" data-testid="customers-table">
              <thead className="bg-slate-50 border-b border-slate-200">
                <tr className="text-left text-xs uppercase tracking-wider text-slate-600">
                  <th className="px-4 py-3">Cliente</th>
                  <th className="px-4 py-3">Piano</th>
                  <th className="px-4 py-3">Eventi</th>
                  <th className="px-4 py-3">Ragione sociale</th>
                  <th className="px-4 py-3">P.IVA / CF</th>
                  <th className="px-4 py-3">Fatturazione</th>
                  <th className="px-4 py-3 text-right">Azioni</th>
                </tr>
              </thead>
              <tbody>
                {users.map((u) => (
                  <Fragment key={u.user_id}>
                    <tr className={`border-b border-slate-100 hover:bg-slate-50/40 ${expanded === u.user_id ? "bg-blue-50/30" : ""}`}>
                      <td className="px-4 py-3">
                        <div className="font-semibold text-[#0F2741]">{u.name || "—"}</div>
                        <div className="text-xs text-slate-500">{u.email}</div>
                      </td>
                      <td className="px-4 py-3">
                        <span className={`text-[10px] uppercase tracking-wider font-bold px-2 py-1 rounded-full border ${planBadgeClass(u.plan)}`}>
                          {u.plan}
                        </span>
                      </td>
                      <td className="px-4 py-3 text-slate-700">{u.events_count}</td>
                      <td className="px-4 py-3 text-slate-700">
                        {u.billing_info?.company_name || <span className="text-slate-400 italic">—</span>}
                      </td>
                      <td className="px-4 py-3 text-slate-700 font-mono text-xs">
                        {u.billing_info?.vat_or_cf || <span className="text-slate-400 italic">—</span>}
                      </td>
                      <td className="px-4 py-3">
                        {u.billing_complete ? (
                          <span className="inline-flex items-center gap-1 text-xs font-semibold text-emerald-700 bg-emerald-50 border border-emerald-200 px-2 py-1 rounded-full">
                            <CheckCircle2 className="w-3 h-3" /> Completi
                          </span>
                        ) : (
                          <span className="inline-flex items-center gap-1 text-xs font-semibold text-amber-700 bg-amber-50 border border-amber-200 px-2 py-1 rounded-full">
                            <AlertCircle className="w-3 h-3" /> Mancanti
                          </span>
                        )}
                      </td>
                      <td className="px-4 py-3 text-right">
                        <button
                          onClick={() => setExpanded(expanded === u.user_id ? null : u.user_id)}
                          className="text-xs text-[#2563EB] hover:underline font-semibold"
                          data-testid={`btn-customer-detail-${u.user_id}`}
                        >
                          {expanded === u.user_id ? "Chiudi" : "Dettagli"}
                        </button>
                      </td>
                    </tr>
                    {expanded === u.user_id && (
                      <tr className="bg-blue-50/30 border-b border-slate-100">
                        <td colSpan="7" className="px-4 py-4">
                          {u.billing_complete ? (
                            <div className="bg-white rounded-lg border border-slate-200 p-4">
                              <div className="flex items-center justify-between mb-3 flex-wrap gap-2">
                                <h4 className="font-display text-lg text-[#0F2741] flex items-center gap-2"><Building2 className="w-5 h-5 text-[#2563EB]" /> Dati di fatturazione</h4>
                                <button onClick={() => copyAll(u.billing_info)} className="text-xs text-[#2563EB] hover:underline inline-flex items-center gap-1">
                                  <Copy className="w-3 h-3" /> Copia tutto
                                </button>
                              </div>
                              <div className="grid grid-cols-1 md:grid-cols-2 gap-3 text-sm">
                                <FactInfo icon={Building2} label="Ragione sociale / Nome" value={u.billing_info.company_name} />
                                <FactInfo icon={Receipt} label="P. IVA / Codice Fiscale" value={u.billing_info.vat_or_cf} mono />
                                <FactInfo icon={MapPin} label="Indirizzo" value={u.billing_info.address} fullW />
                                <FactInfo icon={Mail} label="Email fatturazione" value={u.billing_info.billing_email} />
                                <FactInfo icon={BadgeCheck} label="PEC / Codice SDI" value={u.billing_info.pec_or_sdi} mono />
                                <FactInfo icon={Phone} label="Telefono" value={u.billing_info.phone} />
                              </div>
                              <div className="mt-3 pt-3 border-t border-slate-100 grid grid-cols-2 md:grid-cols-4 gap-3 text-xs">
                                <div><span className="text-slate-500">Stripe Customer:</span> <span className="font-mono text-slate-700">{u.stripe_customer_id || "—"}</span></div>
                                <div><span className="text-slate-500">Registrato:</span> <span className="text-slate-700">{u.created_at ? new Date(u.created_at).toLocaleDateString("it-IT") : "—"}</span></div>
                                <div><span className="text-slate-500">Eventi totali:</span> <strong className="text-slate-800">{u.events_count}</strong></div>
                                <div><span className="text-slate-500">Aggiornati:</span> <span className="text-slate-700">{u.billing_info.updated_at ? new Date(u.billing_info.updated_at).toLocaleDateString("it-IT") : "—"}</span></div>
                              </div>
                            </div>
                          ) : (
                            <div className="bg-amber-50 border border-amber-200 rounded-lg p-4 text-sm text-amber-800">
                              ⚠️ Questo cliente non ha ancora compilato i dati di fatturazione. Non potrà sottoscrivere un piano a pagamento finché non li inserisce.
                            </div>
                          )}
                        </td>
                      </tr>
                    )}
                  </Fragment>
                ))}
              </tbody>
            </table>
          </div>
          <div className="px-4 py-2 text-xs text-slate-500 bg-slate-50 border-t border-slate-200">
            {users.length} cliente{users.length !== 1 ? "i" : ""} · {users.filter((u) => u.billing_complete).length} con dati di fatturazione completi
          </div>
        </div>
      )}
    </div>
  );
}

function FactInfo({ icon: Icon, label, value, mono, fullW }) {
  return (
    <div className={fullW ? "md:col-span-2" : ""}>
      <div className="flex items-center gap-1.5 text-[10px] uppercase tracking-wider text-slate-500 font-semibold">
        {Icon && <Icon className="w-3 h-3" />} {label}
      </div>
      <div className={`text-sm text-slate-800 font-semibold mt-0.5 ${mono ? "font-mono" : ""}`}>
        {value || <span className="text-slate-400 italic font-normal">—</span>}
      </div>
    </div>
  );
}
